/**
 * Recover a dead Session cookie (401 invalid-credential) from a still-signed-in
 * Firebase user. One attempt is shared across concurrent callers. A failed
 * attempt is not retried immediately, so a stuck tab cannot mint in a loop.
 *
 * Silent remint of a still-valid cookie stays in session-remint.ts. This path
 * runs only after the cookie is already rejected. The server then treats login
 * as a first mint and may require a fresh sign-in.
 */

import { classifyLogin403Body } from '@/lib/login-user-messages';

export type SessionRecoveryOutcome = 'reminted' | 'signed-out' | 'unavailable';

/** 401 whose Session recovery did not produce a new cookie. */
export class SessionCredentialError extends Error {
  readonly outcome: Exclude<SessionRecoveryOutcome, 'reminted'>;

  constructor(message: string, outcome: Exclude<SessionRecoveryOutcome, 'reminted'>) {
    super(message);
    this.name = 'SessionCredentialError';
    this.outcome = outcome;
  }
}

const FAILURE_COOLDOWN_MS = 10_000;

let handler: (() => Promise<SessionRecoveryOutcome>) | null = null;
let inFlight: Promise<SessionRecoveryOutcome> | null = null;
let suppressedUntil = 0;

export function isInvalidCredentialFailure(status: number, body: unknown): boolean {
  if (status !== 401) return false;
  if (!body || typeof body !== 'object') return false;
  const reason = (body as { reason?: { kind?: unknown } }).reason;
  return reason?.kind === 'invalid-credential';
}

export function registerSessionRecovery(
  next: () => Promise<SessionRecoveryOutcome>,
): () => void {
  handler = next;
  return () => {
    if (handler === next) handler = null;
  };
}

/** Ask the registered Auth handler to remint or end the Session. */
export async function requestSessionRecovery(): Promise<SessionRecoveryOutcome> {
  if (!handler) return 'unavailable';
  if (Date.now() < suppressedUntil) return 'signed-out';
  if (inFlight) return inFlight;

  const shared = handler()
    .then((outcome) => {
      if (outcome === 'signed-out') {
        suppressedUntil = Date.now() + FAILURE_COOLDOWN_MS;
      }
      return outcome;
    })
    .finally(() => {
      if (inFlight === shared) inFlight = null;
    });
  inFlight = shared;
  return shared;
}

export function resetSessionRecoveryForTests(): void {
  handler = null;
  inFlight = null;
  suppressedUntil = 0;
}

export async function recoverExpiredSession(options: {
  getIdToken: (() => Promise<string>) | null;
  completeLogin: (idToken: string) => Promise<Response>;
  onReminted: (body: unknown) => void;
  onStaleSignIn: () => Promise<void>;
  onDenied: (path: string) => Promise<void>;
}): Promise<SessionRecoveryOutcome> {
  if (!options.getIdToken) {
    await options.onStaleSignIn();
    return 'signed-out';
  }

  let idToken: string;
  try {
    idToken = await options.getIdToken();
  } catch {
    return 'unavailable';
  }

  let response: Response;
  try {
    response = await options.completeLogin(idToken);
  } catch {
    return 'unavailable';
  }

  if (response.ok) {
    const body = await response.json().catch(() => ({}));
    options.onReminted(body);
    return 'reminted';
  }

  if (response.status === 403) {
    const body = await response.json().catch(() => ({}));
    const action = classifyLogin403Body(body);
    if (action.kind === 'stay-on-login') {
      await options.onStaleSignIn();
    } else {
      await options.onDenied(action.path);
    }
    return 'signed-out';
  }

  await options.onStaleSignIn();
  return 'signed-out';
}
